<template>
    <div ref="fileUploadProgressRef" class="File-upload-progress" v-bind="scope.file.attributes">
        <div class="File-progress-header">
            <div class="File-progress-name">{{name}}</div>
            <i class="File-progress-delete isax isax-trash" @click="scope.file.delete()"></i>
        </div>

        <div class="File-progress-size">{{scope.file.size}}</div>

        <Transition name="fade" mode="out-in">
            <div class="File-progress" v-if="!['success','error'].includes(props.status)">
                <div class="File-progress-percentage">{{props.progress}}%</div>
                <div class="File-progress-bar" :style="`--progress-fill-width: ${props.progress}%`">
                    <div class="File-progress-percentage">{{props.progress}}%</div>
                </div>
            </div>
        </Transition>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';

const props = defineProps(
{
    id              : { type: [ String, Number ] },
    name            : { type: String },
    size            : { type: [ String, Number ], default: 0 },
    progress        : { type: Number, default: 0 },
    status          : { type: String },
    toFixed         : { type: Number, defualt: 2 },
    removeCallback  : { type: Function }
});

const fileUploadProgressRef = ref( null );

const getSize = () =>
{
    const sizes = [ 'Bytes', 'KB', 'MB', 'GB', 'TB' ];

    if( props.size === 0 ){ return 'n/a' }

    const i = parseInt( Math.floor( Math.log ( props.size ) / Math.log( 1024 ) ), 10 );

    if( i === 0 ){ return props.size+' '+sizes[i]; }

    return ( props.size / Math.pow(1024, i)).toFixed( props.toFixed )+' '+sizes[i];
}

const scope = computed(() =>
{
    return (
    {
        file:
        {
            size   : getSize(),
            delete : () =>
            {
                if( props.removeCallback )
                {
                    props.removeCallback();
                }
            },
            attributes:
            {
                class:
                {
                    'File-upload-progress'         : true,
                    'File-upload-progress-success' : props.status === 'success',
                    'File-upload-progress-error'   : props.status === 'error'
                }
            }
        }
    })
});

onMounted( () =>
{
    fileUploadProgressRef.value.setAttribute( 'style', `--progress-container-width: ${fileUploadProgressRef.value.offsetWidth}px` );
});
</script>

<style lang="scss">
.File-upload-progress
{
    border: 1px solid var(--input-border-color);

    border-radius: 4px;

    padding: px(12);

    &.File-upload-progress-error
    {
        color: var(--input-error-text-color);
        background-color: var(--input-error-background-color);
        border: 1px solid var(--input-error-border-color);
    }

    .File-progress-header
    {
        display: flex;
        justify-content: space-between;
        flex-direction: row;

        gap: px(8);

        .File-progress-name
        {
            @include font-body-md-semibold;

            line-height: px(32);

            margin-top: px(-3);

            flex: 1;
            min-width: 0;

            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        .File-progress-delete
        {
            @include font-title-sm-regular;

            width: px(32);
            height: px(32);

            text-align: center;

            color: var(--secondary-text-color);
            background: transparent;

            border-radius: 50%;

            cursor: pointer;

            &:hover
            {
                color: var(--primary-text-color);
                background: var(--layout-border-color);
            }
        }
    }

    .File-progress-size
    {
        @include font-body-xs-regular;

        margin-top: px(-11);
        margin-bottom: px(8);
    }

    .File-progress
    {
        --progress-width: calc( var(--progress-container-width) - #{px(24)} );

        position: relative;

        width: var(--progress-width);
        height: px(20);

        margin: auto;

        background-color: var(--layout-border-color);

        border-radius: 8px;

        overflow: hidden;

        .File-progress-percentage
        {
            @include font-body-md-semibold;

            position: absolute;

            top: 0;
            left: 0;
            right: 0;

            width: var(--progress-width);

            text-align: center;

            line-height: px(20);

            color: var(--brand-colors-ramp-blue);
        }

        .File-progress-bar
        {
            position: relative;

            width: var(--progress-fill-width);
            height: 100%;

            background-color: var(--brand-colors-ramp-blue);

            overflow: hidden;

            transition: width 0.3s ease-in-out;

            .File-progress-percentage
            {
                color: white;

                z-index: 2;
            }
        }
    }
}
</style>